// ─────────────────────────────────────────────────────────────────────────
// COUCHE 1 — Filtre d'éligibilité (hard constraints)
//   Réf. « Note experte — Architecture de Matching » (Arié & Max, juillet 2026).
//   Ces critères ne sont JAMAIS pondérés : un seul échec exclut la paire des
//   suggestions. Fonction pure — aucune dépendance Firestore (données passées
//   en paramètre).
//
//   Décisions produit actées avec Max (2026-07-15) :
//   - PAS de gate de complétion : la complétion de profil ne bloque jamais la
//     candidature (elle reste un simple avertissement ailleurs dans l'UI).
//   - Quota : Option A — on bloque seulement si le festival ENTIER est complet
//     (capacité globale). Le quota PAR CATÉGORIE nécessite un champ dédié dans
//     standDetails → laissé en TODO (voir `category_full`).
// ─────────────────────────────────────────────────────────────────────────

export type EligibilityReason =
  | 'dates_passed'      // le festival est terminé
  | 'category_not_accepted' // le type de stand n'est pas proposé par le festival
  | 'festival_full'    // plus aucune place (capacité globale épuisée)
  | 'technical_mismatch'; // le festival ne peut pas fournir les besoins techniques du stand

export interface EligibilityResult {
  eligible: boolean;
  reasons: EligibilityReason[];
}

// Libellés lisibles pour l'exposant (pourquoi une suggestion n'apparaît pas).
export const ELIGIBILITY_LABELS: Record<EligibilityReason, string> = {
  dates_passed: 'Festival terminé',
  category_not_accepted: "Votre type de stand n'est pas accepté sur ce festival",
  festival_full: 'Toutes les places sont prises',
  technical_mismatch: 'Le festival ne peut pas fournir les services techniques requis',
};

interface StandLike {
  standType?: string;
  waterNeeds?: boolean;
  furnitureNeeds?: boolean | string;
}

interface FestivalLike {
  endDate?: string;
  standDetails?: Record<string, unknown>;
  totalStands?: number;
  technical?: {
    hasDrinkingWater?: boolean;
    providesTables?: boolean;
  };
}

/** Le festival accepte-t-il ce type de stand ? (clé exacte ou préfixée `type-…`) */
function acceptsCategory(festival: FestivalLike, standType: string): boolean {
  if (!festival.standDetails) return false;
  return Object.keys(festival.standDetails).some(
    k => k === standType || k.startsWith(standType + '-')
  );
}

/** Le festival peut-il fournir les besoins techniques du stand ? */
function canMeetTechnicalNeeds(stand: StandLike, festival: FestivalLike): boolean {
  const tech = festival.technical || {};

  // Si l'expo a besoin d'eau et le festival ne peut pas en fournir → incompatible
  if (stand.waterNeeds && !tech.hasDrinkingWater) {
    return false;
  }

  // Si l'expo a besoin de mobilier/tables et le festival ne peut pas en fournir → incompatible
  if (stand.furnitureNeeds && !tech.providesTables) {
    return false;
  }

  return true;
}

/**
 * Détermine si une paire (stand, festival) peut être suggérée / faire l'objet
 * d'une candidature. Retourne toutes les raisons d'exclusion (pour affichage).
 *
 * @param now  horodatage courant (ms) — injecté pour rester déterministe/testable.
 */
export function isEligible(
  stand: StandLike,
  festival: FestivalLike,
  now: number = Date.now()
): EligibilityResult {
  const reasons: EligibilityReason[] = [];

  // 1. Dates — le festival ne doit pas être terminé.
  //    (La disponibilité PAR DATES de l'exposant — calendrier perso — n'est pas
  //     encore modélisée : TODO quand le CalendarWidget exposant sera relié.)
  const end = festival.endDate ? new Date(festival.endDate).getTime() : NaN;
  if (!Number.isFinite(end) || end < now) {
    reasons.push('dates_passed');
  }

  // 2. Catégorie — le type de stand doit être proposé par le festival.
  if (!stand.standType || !acceptsCategory(festival, stand.standType)) {
    reasons.push('category_not_accepted');
  }

  // 3. Capacité globale — si le festival est complet, on ne suggère plus.
  //    TODO(quota par catégorie) : quand standDetails portera un compteur de
  //    places restantes par catégorie, ajouter ici `category_full`.
  if (festival.totalStands === 0) {
    reasons.push('festival_full');
  }

  // 4. Besoins techniques — le festival doit pouvoir fournir ce que l'expo demande.
  //    (électricité est par catégorie, eau/mobilier sont globaux au festival)
  if (!canMeetTechnicalNeeds(stand, festival)) {
    reasons.push('technical_mismatch');
  }

  return { eligible: reasons.length === 0, reasons };
}
